import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import GradeCardService from "../../_services/GradeCardService";

interface Structure {
  Id: number;
  Name: string;
  Description: string | null;
  ProgramIds: number[] | null;
  TermCount: number;
  IsBuiltIn: number;
  EnableStatus: number;
  DefaultRatingScaleName: string | null;
}

const GradeCardTemplatesPage: React.FC = () => {
  const history = useHistory();
  const [structures, setStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Structure | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchStructures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await GradeCardService.listStructures();
      setStructures(res.data?.data || []);
    } catch (e: any) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStructures();
  }, [fetchStructures]);

  const handleClone = async (id: number) => {
    try {
      const res = await GradeCardService.cloneStructure(id);
      if (res.data?.status === 1) {
        fetchStructures();
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || "Clone failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      const res = await GradeCardService.deleteStructure(deleteTarget.Id);
      if (res.data?.status === 1) {
        setDeleteTarget(null);
        fetchStructures();
      } else {
        setDeleteError(res.data?.message || "Delete failed");
      }
    } catch (e: any) {
      setDeleteError(e?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h4 style={{ margin: 0 }}>Grade Card Templates</h4>
        <Button
          variant="primary"
          size="sm"
          onClick={() => history.push("/grade-card-templates/new")}
        >
          + New Template
        </Button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : structures.length === 0 ? (
        <div className="text-center py-4 text-muted">
          No grade card templates found. Create one to get started.
        </div>
      ) : (
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>Rating Scale</th>
              <th style={{ width: 80 }}>Terms</th>
              <th style={{ width: 100 }}>Type</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {structures.map((s) => (
              <tr key={s.Id}>
                <td>
                  {s.Name}
                  {s.Description && (
                    <div
                      style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 2 }}
                    >
                      {s.Description}
                    </div>
                  )}
                </td>
                <td>{s.DefaultRatingScaleName || "—"}</td>
                <td>{s.TermCount}</td>
                <td>
                  {s.IsBuiltIn ? (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        fontSize: 11,
                        borderRadius: 4,
                        background: "var(--brand-primary-subtle)",
                        color: "#3b5998",
                      }}
                    >
                      Built-in
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        fontSize: 11,
                        borderRadius: 4,
                        background: "#e8f5e9",
                        color: "#2e7d32",
                      }}
                    >
                      Custom
                    </span>
                  )}
                </td>
                <td>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="me-1"
                    onClick={() =>
                      history.push(`/grade-card-templates/${s.Id}/edit`)
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="me-1"
                    onClick={() => handleClone(s.Id)}
                  >
                    Clone
                  </Button>
                  {!s.IsBuiltIn && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteTarget(s)}
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Delete confirmation modal */}
      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{deleteTarget?.Name}</strong>?
          {deleteError && (
            <div className="alert alert-danger mt-2">{deleteError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GradeCardTemplatesPage;
